import { assert } from '@disy/lib/custom-error';

import type {
  AddAcquisitionLayerCommandPayload,
  AddAttributeCommandPayload,
  AddAttributesCommandPayload,
  AddConditionCommandPayload,
  AddConditionGroupCommandPayload,
  AddCuratedReportCommandPayload,
  AddDataViewCommandPayload,
  AddDataViewConditionCommandPayload,
  AddDataViewToDataViewConnectionCommandPayload,
  AddDrillThroughCommandPayload,
  AddEmbeddingTargetCommandPayload,
  AddEmptyMapViewCommandPayload,
  AddExternalLayersCommandPayload,
  AddGridSquareLayerCommandPayload,
  AddLayerGroupCommandPayload,
  AddNewViewWithDataViewCommandPayload,
  AddObjecttypesCommandPayload,
  AddPreconditionAttributeCommandPayload,
  AddQlConditionCommandPayload,
  AddReferenceLineCommandPayload,
  AddSelfServiceLayerCommandPayload,
  AddSketchLayerCommandPayload,
  AddSpatialConditionCommandPayload,
  AddTimelineConnectingLinesLayerCommandPayload,
  AddTrendLineCommandPayload,
  AddViewCommandPayload,
  AddViewToDataViewConnectionCommandPayload,
  AddWorksheetCommandPayload,
  ApplyAnalysisContextToLayerCommandPayload,
  ApplyStableClassificationsCommandPayload,
  BindOverviewMapViewCommandPayload,
  ChangeAttributeBundleSettingsPayload,
  ChangeConditionGroupSettingsCommandPayload,
  ChangeConditionSettingsCommandPayload,
  ChangeCuratedReportCommandPayload,
  ChangeCurrentExtentCommandPayload,
  ChangeDataViewConditionCommandPayload,
  ChangeDataViewConditionSettingCommandPayload,
  ChangeDataViewSettingsCommandPayload,
  ChangeExternalLayerCredentialsCommandPayload,
  ChangeHierarchyLevelCommandPayload,
  ChangeLayerSettingsCommandPayload,
  ChangeLayerStructureTypeCommandPayload,
  ChangeMainObjecttypeCommandPayload,
  ChangeMaxExtentCommandPayload,
  ChangeTableCellGroupStateCommandPayload,
  ChangeViewSettingsCommandPayload,
  ChangeVisualizationCommandPayload,
  ChangeWorkbookSettingsPayload,
  ChangeWorksheetSettingsCommandPayload,
  CompositeCommandPayload,
  ConvertConditionToQlCommandPayload,
  CopyViewCommandPayload,
  CopyWorksheetCommandPayload,
  CouplingMapViewCommandPayload,
  CreateDateFilterCommandPayload,
  CreateFilterFromSelectionCommandPayload,
  CreateGeocodingReportCommandPayload,
  CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload,
  CreateLocalCalculatedAttributeCommandPayload,
  DecoupleFromMapViewCouplingGroupCommandPayload,
  DetachCatalogLayerCommandPayload,
  DetachViewCommandPayload,
  DrillDownFromSelectionCommandPayload,
  EditDrillThroughCommandPayload,
  EditEmbeddingTargetCommandPayload,
  EditLocalCalculatedAttributeCommandPayload,
  ExtractDataViewToNewViewCommandPayload,
  ImportCatalogLayerCommandPayload,
  IntegrateViewCommandPayload,
  InvertSelectionCommandPayload,
  MoveDataViewToViewCommandPayload,
  MoveLayerCommandPayload,
  RebaseCommandPayload,
  RemoveAttributeCommandPayload,
  RemoveConditionCommandPayload,
  RemoveCuratedReportCommandPayload,
  RemoveDataViewAttributeModificationCommandPayload,
  RemoveDataViewCommandPayload,
  RemoveDataViewConditionCommandPayload,
  RemoveDataViewToDataViewConnectionCommandPayload,
  RemoveDrillThroughCommandPayload,
  RemoveEmbeddingTargetCommandPayload,
  RemoveGeometryFromSpatialConditionCommandPayload,
  RemoveLayerCommandPayload,
  RemoveLocalAttributeCommandPayload,
  RemoveObjecttypesCommandPayload,
  RemoveViewCommandPayload,
  RemoveViewToDataViewConnectionCommandPayload,
  RemoveWorkbookAttributeModificationCommandPayload,
  RemoveWorksheetCommandPayload,
  RenameWorksheetCommandPayload,
  ReorderAttributesCommandPayload,
  ReorderConditionsCommandPayload,
  ReorderDataViewConditionSCommandPayload,
  ReorderDataViewsCommandPayload,
  ReorderLayersCommandPayload,
  ReorderWorksheetsCommandPayload,
  ResetDataViewAttributeModificationCommandPayload,
  ResetQlConditionCommandPayload,
  ResetWorkbookAttributeModificationCommandPayload,
  SelectionByGeometryIdCommandPayload,
  SetConditionCommandPayload,
  SetConditionSettingCommandPayload,
  SetControllerViewValueCommandPayload,
  SetDataViewAttributeHighlightingRuleCommandPayload,
  SetDataViewAttributeIntervalClassificationCommandPayload,
  SetDataViewAttributeModificationCommandPayload,
  SetDataViewAttributeUniqueValueClassificationCommandPayload,
  SetQlConditionCommandPayload,
  SetSelectionCommandPayload,
  SetSelectionExternalLayerCommandPayload,
  SetSelectionFromOtherDataViewCommandPayload,
  SetSelectionFromOtherExternalLayerCommandPayload,
  SetSelectionInOtherLayersCommandPayload,
  SetSpatialConditionCommandPayload,
  SetViewLayoutCommandPayload,
  SetWorkbookAttributeModificationCommandPayload,
  SortDataViewByAttributeCommandPayload,
  TransferConditionsCommandPayload,
  TransferSelectionFromDrillThroughCommandPayload,
  UngroupConditionGroupCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';

export class Command<P extends object = object & { ignoreInOnDemand?: boolean }> {
  static CHANGE_WORKBOOK_SETTINGS = commandFunction<ChangeWorkbookSettingsPayload>('CHANGE_WORKBOOK_SETTINGS');

  static ADD_WORKSHEET = commandFunction<AddWorksheetCommandPayload>('ADD_WORKSHEET');
  static COPY_WORKSHEET = commandFunction<CopyWorksheetCommandPayload>('COPY_WORKSHEET');
  static CHANGE_WORKSHEET_SETTINGS =
    commandFunction<ChangeWorksheetSettingsCommandPayload>('CHANGE_WORKSHEET_SETTINGS');
  static REMOVE_WORKSHEET = commandFunction<RemoveWorksheetCommandPayload>('REMOVE_WORKSHEET');
  static RENAME_WORKSHEET = commandFunction<RenameWorksheetCommandPayload>('RENAME_WORKSHEET');
  static REORDER_WORKSHEETS = commandFunction<ReorderWorksheetsCommandPayload>('REORDER_WORKSHEETS');

  static ADD_VIEW = commandFunction<AddViewCommandPayload>('ADD_VIEW');
  static ADD_EMPTY_MAP_VIEW = commandFunction<AddEmptyMapViewCommandPayload>('ADD_EMPTY_MAP_VIEW');
  static ADD_NEW_VIEW_WITH_DATA_VIEW =
    commandFunction<AddNewViewWithDataViewCommandPayload>('ADD_NEW_VIEW_WITH_DATA_VIEW');
  static EXTRACT_DATA_VIEW_TO_NEW_VIEW = commandFunction<ExtractDataViewToNewViewCommandPayload>(
    'EXTRACT_DATA_VIEW_TO_NEW_VIEW',
  );
  static MOVE_DATA_VIEW_TO_VIEW = commandFunction<MoveDataViewToViewCommandPayload>('MOVE_DATA_VIEW_TO_VIEW');
  static REMOVE_VIEW = commandFunction<RemoveViewCommandPayload>('REMOVE_VIEW');
  static COPY_VIEW = commandFunction<CopyViewCommandPayload>('COPY_VIEW');
  static SET_VIEW_LAYOUT = commandFunction<SetViewLayoutCommandPayload>('SET_VIEW_LAYOUT');
  static CHANGE_VIEW_SETTINGS = commandFunction<ChangeViewSettingsCommandPayload>('CHANGE_VIEW_SETTINGS');
  static CHANGE_CURRENT_EXTENT = commandFunction<ChangeCurrentExtentCommandPayload>('CHANGE_CURRENT_EXTENT');
  static CHANGE_MAX_EXTENT = commandFunction<ChangeMaxExtentCommandPayload>('CHANGE_MAX_EXTENT');
  static ADD_DATA_VIEW = commandFunction<AddDataViewCommandPayload>('ADD_DATA_VIEW');
  static REMOVE_DATA_VIEW = commandFunction<RemoveDataViewCommandPayload>('REMOVE_DATA_VIEW');
  static REORDER_DATA_VIEWS = commandFunction<ReorderDataViewsCommandPayload>('REORDER_DATA_VIEWS');
  static CHANGE_VISUALIZATION = commandFunction<ChangeVisualizationCommandPayload>('CHANGE_VISUALIZATION');
  static CHANGE_DATA_VIEW_SETTINGS = commandFunction<ChangeDataViewSettingsCommandPayload>('CHANGE_DATA_VIEW_SETTINGS');
  static CHANGE_MAIN_OBJECTTYPE = commandFunction<ChangeMainObjecttypeCommandPayload>('CHANGE_MAIN_OBJECTTYPE');

  static ADD_LAYER_GROUP = commandFunction<AddLayerGroupCommandPayload>('ADD_LAYER_GROUP');
  static IMPORT_CATALOG_LAYER = commandFunction<ImportCatalogLayerCommandPayload>('IMPORT_CATALOG_LAYER');
  static DETACH_CATALOG_LAYER = commandFunction<DetachCatalogLayerCommandPayload>('DETACH_CATALOG_LAYER');
  static APPLY_ANALYSIS_CONTEXT_TO_LAYER = commandFunction<ApplyAnalysisContextToLayerCommandPayload>(
    'APPLY_ANALYSIS_CONTEXT_TO_LAYER',
  );

  static ADD_OBJECTTYPES = commandFunction<AddObjecttypesCommandPayload>('ADD_OBJECTTYPES');
  static REMOVE_OBJECTTYPES = commandFunction<RemoveObjecttypesCommandPayload>('REMOVE_OBJECTTYPES');

  static ADD_ATTRIBUTE = commandFunction<AddAttributeCommandPayload>('ADD_ATTRIBUTE');
  static ADD_ATTRIBUTES = commandFunction<AddAttributesCommandPayload>('ADD_ATTRIBUTES');
  static ADD_PRECONDITION_ATTRIBUTE =
    commandFunction<AddPreconditionAttributeCommandPayload>('ADD_PRECONDITION_ATTRIBUTE');
  static REMOVE_ATTRIBUTE = commandFunction<RemoveAttributeCommandPayload>('REMOVE_ATTRIBUTE');
  static REORDER_ATTRIBUTES = commandFunction<ReorderAttributesCommandPayload>('REORDER_ATTRIBUTES');

  static SET_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<SetDataViewAttributeModificationCommandPayload>(
    'SET_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  );
  static REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<RemoveDataViewAttributeModificationCommandPayload>(
    'REMOVE_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  );
  static RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION = commandFunction<ResetDataViewAttributeModificationCommandPayload>(
    'RESET_DATA_VIEW_ATTRIBUTE_MODIFICATION',
  );
  static SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES =
    commandFunction<SetDataViewAttributeHighlightingRuleCommandPayload>('SET_DATA_VIEW_ATTRIBUTE_HIGHLIGHTING_RULES');
  static SET_DATA_VIEW_ATTRIBUTE_UNIQUE_VALUES_CLASSIFICATION =
    commandFunction<SetDataViewAttributeUniqueValueClassificationCommandPayload>(
      'SET_DATA_VIEW_ATTRIBUTE_UNIQUE_VALUES_CLASSIFICATION',
    );
  static SET_DATA_VIEW_ATTRIBUTE_INTERVAL_CLASSIFICATION =
    commandFunction<SetDataViewAttributeIntervalClassificationCommandPayload>(
      'SET_DATA_VIEW_ATTRIBUTE_INTERVAL_CLASSIFICATION',
    );
  static SORT_DATA_VIEW_BY_ATTRIBUTE =
    commandFunction<SortDataViewByAttributeCommandPayload>('SORT_DATA_VIEW_BY_ATTRIBUTE');

  static ADD_CONDITION = commandFunction<AddConditionCommandPayload>('ADD_CONDITION');
  static SET_CONDITION = commandFunction<SetConditionCommandPayload>('SET_CONDITION');
  static SET_CONDITION_SETTING = commandFunction<SetConditionSettingCommandPayload>('SET_CONDITION_SETTING');
  static REORDER_CONDITIONS = commandFunction<ReorderConditionsCommandPayload>('REORDER_CONDITIONS');
  static REMOVE_CONDITION = commandFunction<RemoveConditionCommandPayload>('REMOVE_CONDITION');
  static CONVERT_CONDITION_TO_QL = commandFunction<ConvertConditionToQlCommandPayload>('CONVERT_CONDITION_TO_QL');
  static ADD_SPATIAL_CONDITION = commandFunction<AddSpatialConditionCommandPayload>('ADD_SPATIAL_CONDITION');
  static SET_SPATIAL_CONDITION = commandFunction<SetSpatialConditionCommandPayload>('SET_SPATIAL_CONDITION');
  static REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION = commandFunction<RemoveGeometryFromSpatialConditionCommandPayload>(
    'REMOVE_GEOMETRY_FROM_SPATIAL_CONDITION',
  );

  static CHANGE_CONDITION_SETTINGS =
    commandFunction<ChangeConditionSettingsCommandPayload>('CHANGE_CONDITION_SETTINGS');
  static ADD_CONDITION_GROUP = commandFunction<AddConditionGroupCommandPayload>('ADD_CONDITION_GROUP');
  static UNGROUP_CONDITION_GROUP = commandFunction<UngroupConditionGroupCommandPayload>('UNGROUP_CONDITION_GROUP');

  static CHANGE_CONDITION_GROUP_SETTINGS = commandFunction<ChangeConditionGroupSettingsCommandPayload>(
    'CHANGE_CONDITION_GROUP_SETTINGS',
  );
  static ADD_DATA_VIEW_CONDITION = commandFunction<AddDataViewConditionCommandPayload>('ADD_DATA_VIEW_CONDITION');
  static REORDER_DATA_VIEW_CONDITIONS =
    commandFunction<ReorderDataViewConditionSCommandPayload>('REORDER_DATA_VIEW_CONDITIONS');
  static REMOVE_DATA_VIEW_CONDITION =
    commandFunction<RemoveDataViewConditionCommandPayload>('REMOVE_DATA_VIEW_CONDITION');
  static CHANGE_DATA_VIEW_CONDITION =
    commandFunction<ChangeDataViewConditionCommandPayload>('CHANGE_DATA_VIEW_CONDITION');
  static CHANGE_DATA_VIEW_CONDITION_SETTING = commandFunction<ChangeDataViewConditionSettingCommandPayload>(
    'CHANGE_DATA_VIEW_CONDITION_SETTING',
  );

  static ADD_QL_CONDITION = commandFunction<AddQlConditionCommandPayload>('ADD_QL_CONDITION');
  static SET_QL_CONDITION = commandFunction<SetQlConditionCommandPayload>('SET_QL_CONDITION');
  static RESET_QL_CONDITION = commandFunction<ResetQlConditionCommandPayload>('RESET_QL_CONDITION');

  static CHANGE_HIERARCHY_LEVEL = commandFunction<ChangeHierarchyLevelCommandPayload>('CHANGE_HIERARCHY_LEVEL');

  static ADD_DRILLTHROUGH = commandFunction<AddDrillThroughCommandPayload>('ADD_DRILLTHROUGH');
  static EDIT_DRILLTHROUGH = commandFunction<EditDrillThroughCommandPayload>('EDIT_DRILLTHROUGH');
  static REMOVE_DRILLTHROUGH = commandFunction<RemoveDrillThroughCommandPayload>('REMOVE_DRILLTHROUGH');

  static ADD_EMBEDDING_TARGET = commandFunction<AddEmbeddingTargetCommandPayload>('ADD_EMBEDDING_TARGET');
  static EDIT_EMBEDDING_TARGET = commandFunction<EditEmbeddingTargetCommandPayload>('EDIT_EMBEDDING_TARGET');
  static REMOVE_EMBEDDING_TARGET = commandFunction<RemoveEmbeddingTargetCommandPayload>('REMOVE_EMBEDDING_TARGET');

  static SET_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<SetWorkbookAttributeModificationCommandPayload>(
    'SET_WORKBOOK_ATTRIBUTE_MODIFICATION',
  );
  static REMOVE_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<RemoveWorkbookAttributeModificationCommandPayload>(
    'REMOVE_WORKBOOK_ATTRIBUTE_MODIFICATION',
  );
  static RESET_WORKBOOK_ATTRIBUTE_MODIFICATION = commandFunction<ResetWorkbookAttributeModificationCommandPayload>(
    'RESET_WORKBOOK_ATTRIBUTE_MODIFICATION',
  );

  static CREATE_LOCAL_ATTRIBUTE_WITH_MODIFIED_OLAP_ATTRIBUTE_DEFINITION =
    commandFunction<CreateLocalAttributeWithModifiedOlapAttributeDefinitionCommandPayload>(
      'CREATE_LOCAL_ATTRIBUTE_WITH_MODIFIED_OLAP_ATTRIBUTE_DEFINITION',
    );
  static CREATE_LOCAL_CALCULATED_ATTRIBUTE = commandFunction<CreateLocalCalculatedAttributeCommandPayload>(
    'CREATE_LOCAL_CALCULATED_ATTRIBUTE',
  );
  static EDIT_LOCAL_CALCULATED_ATTRIBUTE = commandFunction<EditLocalCalculatedAttributeCommandPayload>(
    'EDIT_LOCAL_CALCULATED_ATTRIBUTE',
  );
  static REMOVE_LOCAL_ATTRIBUTE = commandFunction<RemoveLocalAttributeCommandPayload>('REMOVE_LOCAL_ATTRIBUTE');

  static CHANGE_LAYER_STRUCTURE_TYPE =
    commandFunction<ChangeLayerStructureTypeCommandPayload>('CHANGE_LAYER_STRUCTURE_TYPE');
  static CHANGE_LAYER_SETTINGS = commandFunction<ChangeLayerSettingsCommandPayload>('CHANGE_LAYER_SETTINGS');
  static REORDER_LAYERS = commandFunction<ReorderLayersCommandPayload>('REORDER_LAYERS');
  static MOVE_LAYER = commandFunction<MoveLayerCommandPayload>('MOVE_LAYER');

  static ADD_ACQUISITION_LAYER = commandFunction<AddAcquisitionLayerCommandPayload>('ADD_ACQUISITION_LAYER');

  static CHANGE_TABLE_CELL_GROUP_STATE = commandFunction<ChangeTableCellGroupStateCommandPayload>(
    'CHANGE_TABLE_CELL_GROUP_STATE',
  );
  static SET_SELECTION = commandFunction<SetSelectionCommandPayload>('SET_SELECTION');
  static SET_SELECTION_EXTERNAL_LAYER =
    commandFunction<SetSelectionExternalLayerCommandPayload>('SET_SELECTION_EXTERNAL_LAYER');
  static SET_SELECTION_FROM_OTHER_DATA_VIEW = commandFunction<SetSelectionFromOtherDataViewCommandPayload>(
    'SET_SELECTION_FROM_OTHER_DATA_VIEW',
  );
  static SET_SELECTION_FROM_OTHER_EXTERNAL_LAYER = commandFunction<SetSelectionFromOtherExternalLayerCommandPayload>(
    'SET_SELECTION_FROM_OTHER_EXTERNAL_LAYER',
  );
  static SET_SELECTION_IN_OTHER_LAYERS = commandFunction<SetSelectionInOtherLayersCommandPayload>(
    'SET_SELECTION_IN_OTHER_LAYERS',
  );
  static SELECTION_BY_GEOMETRY_ID = commandFunction<SelectionByGeometryIdCommandPayload>('SELECTION_BY_GEOMETRY_ID');
  static INVERT_SELECTION = commandFunction<InvertSelectionCommandPayload>('INVERT_SELECTION');
  static CREATE_FILTER_FROM_SELECTION =
    commandFunction<CreateFilterFromSelectionCommandPayload>('CREATE_FILTER_FROM_SELECTION');
  static DRILL_DOWN_FROM_SELECTION = commandFunction<DrillDownFromSelectionCommandPayload>('DRILL_DOWN_FROM_SELECTION');
  static TRANSFER_CONDITIONS = commandFunction<TransferConditionsCommandPayload>('TRANSFER_CONDITIONS');
  static TRANSFER_SELECTION_FROM_DRILL_THROUGH = commandFunction<TransferSelectionFromDrillThroughCommandPayload>(
    'TRANSFER_SELECTION_FROM_DRILL_THROUGH',
  );

  static REMOVE_LAYER = commandFunction<RemoveLayerCommandPayload>('REMOVE_LAYER');

  static ADD_EXTERNAL_LAYERS = commandFunction<AddExternalLayersCommandPayload>('ADD_EXTERNAL_LAYERS');
  static CHANGE_EXTERNAL_LAYER_CREDENTIALS = commandFunction<ChangeExternalLayerCredentialsCommandPayload>(
    'CHANGE_EXTERNAL_LAYER_CREDENTIALS',
  );

  static CREATE_GEOCODING_REPORT = commandFunction<CreateGeocodingReportCommandPayload>('CREATE_GEOCODING_REPORT');

  static ADD_SKETCH_LAYER = commandFunction<AddSketchLayerCommandPayload>('ADD_SKETCH_LAYER');

  static CHANGE_ATTRIBUTE_BUNDLE_SETTINGS = commandFunction<ChangeAttributeBundleSettingsPayload>(
    'CHANGE_ATTRIBUTE_BUNDLE_SETTINGS',
  );

  static CREATE_DATE_FILTER = commandFunction<CreateDateFilterCommandPayload>('CREATE_DATE_FILTER');

  static REMOVE_CURATED_REPORT = commandFunction<RemoveCuratedReportCommandPayload>('REMOVE_CURATED_REPORT');

  static ADD_CURATED_REPORT = commandFunction<AddCuratedReportCommandPayload>('ADD_CURATED_REPORT');

  static CHANGE_CURATED_REPORT = commandFunction<ChangeCuratedReportCommandPayload>('CHANGE_CURATED_REPORT');

  static COMPOSITE_COMMAND = commandFunction<CompositeCommandPayload>('COMPOSITE_COMMAND');

  static ADD_GRID_SQUARE_LAYER = commandFunction<AddGridSquareLayerCommandPayload>('ADD_GRID_SQUARE_LAYER');

  static ADD_SELF_SERVICE_LAYER = commandFunction<AddSelfServiceLayerCommandPayload>('ADD_SELF_SERVICE_LAYER');

  static ADD_TIMELINE_CONNECTING_LINES_LAYER = commandFunction<AddTimelineConnectingLinesLayerCommandPayload>(
    'ADD_TIMELINE_CONNECTING_LINES_LAYER',
  );

  static CHANGE_MAP_VIEW_COUPLING = commandFunction<CouplingMapViewCommandPayload>('CHANGE_MAP_VIEW_COUPLING');
  static DECOUPLE_FROM_MAP_VIEW_COUPLING = commandFunction<DecoupleFromMapViewCouplingGroupCommandPayload>(
    'DECOUPLE_FROM_MAP_VIEW_COUPLING',
  );
  static BIND_OVERVIEW_MAP_VIEW = commandFunction<BindOverviewMapViewCommandPayload>('BIND_OVERVIEW_MAP_VIEW');
  static INTEGRATE_VIEW = commandFunction<IntegrateViewCommandPayload>('INTEGRATE_VIEW');
  static DETACH_VIEW = commandFunction<DetachViewCommandPayload>('DETACH_VIEW');

  static SET_CONTROLLER_VIEW_VALUE = commandFunction<SetControllerViewValueCommandPayload>('SET_CONTROLLER_VIEW_VALUE');

  static REBASE = commandFunction<RebaseCommandPayload>('REBASE');

  static readonly ADD_DATA_VIEW_CONNECTION =
    commandFunction<AddDataViewToDataViewConnectionCommandPayload>('ADD_DATA_VIEW_CONNECTION');
  static readonly REMOVE_DATA_VIEW_CONNECTION =
    commandFunction<RemoveDataViewToDataViewConnectionCommandPayload>('REMOVE_DATA_VIEW_CONNECTION');
  static readonly ADD_VIEW_TO_DATA_VIEW_CONNECTION = commandFunction<AddViewToDataViewConnectionCommandPayload>(
    'ADD_VIEW_TO_DATA_VIEW_CONNECTION',
  );
  static readonly REMOVE_VIEW_TO_DATA_VIEW_CONNECTION = commandFunction<RemoveViewToDataViewConnectionCommandPayload>(
    'REMOVE_VIEW_TO_DATA_VIEW_CONNECTION',
  );
  static readonly APPLY_STABLE_CLASSIFICATIONS =
    commandFunction<ApplyStableClassificationsCommandPayload>('APPLY_STABLE_CLASSIFICATIONS');
  static readonly ADD_REFERENCE_LINE_DATA_VIEW =
    commandFunction<AddReferenceLineCommandPayload>('ADD_REFERENCE_LINE_DATA_VIEW');
  static readonly ADD_TREND_LINE_DATA_VIEW = commandFunction<AddTrendLineCommandPayload>('ADD_TREND_LINE_DATA_VIEW');

  constructor(type: string, payload: P) {
    assert(type, 'type is mandatory');
    assert(type in Command, `unknown command type ${type}`);
    this.type = type;
    this.payload = payload;
  }

  /** the type of the command as {string} */
  readonly type: string;

  /** payload The data to send with the command */
  readonly payload: P;

  /**
   * Creates a {@link Command.COMPOSITE_COMMAND} of this and the given command in that order.
   * If this command is an {@link Command.COMPOSITE_COMMAND} already, the given command is
   * added to the tail of the containing commands.
   *
   * @param command - The command to append as a follow-up command
   * @return A {@link Command.COMPOSITE_COMMAND} command
   */
  append(command: Command): Command<CompositeCommandPayload> {
    if (this.type === Command.COMPOSITE_COMMAND.type) {
      const compositeCommand = this as unknown as Command<CompositeCommandPayload>;
      compositeCommand.payload.commands.push(command);
      return compositeCommand;
    } else {
      return Command.COMPOSITE_COMMAND({ commands: [this, command] });
    }
  }
}

// Exported for testing
export type CommandFunctionType<P extends object> = ((payload: P) => Command<P>) & { readonly type: string };

function commandFunction<P extends object>(type: string) {
  const func = (payload: P) => new Command<P>(type, payload);
  func.type = type;
  return func as CommandFunctionType<P>;
}

export function isCommandType<P extends object>(
  command: Command,
  commandCreator: ReturnType<typeof commandFunction<P>>,
): command is Command<P> {
  return command.type === commandCreator.type;
}
